import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRightLeft, Download, Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DOC_CONFIG } from "./config";
import { DocumentForm } from "./DocumentForm";
import { DocumentView } from "./DocumentView";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const SOURCE = {
  proformas: { kind: "quotations", label: "quotation", button: "From quotation" },
  invoices: { kind: "proformas", label: "proforma invoice", button: "From proforma" },
};

export default function Documents({ kind }) {
  const cfg = DOC_CONFIG[kind];
  const { can, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewId, setViewId] = useState(params.get("open") || null);
  const [presetLead, setPresetLead] = useState(params.get("lead") || "");
  const src = SOURCE[kind];
  const [srcOpen, setSrcOpen] = useState(false);
  const [srcRows, setSrcRows] = useState(null);

  const openSource = async () => {
    setSrcOpen(true); setSrcRows(null);
    try {
      const { data } = await api.get(`/documents/${src.kind}`, { params: { limit: 100 } });
      setSrcRows(data.items);
    } catch (e) { toast.error(errMsg(e)); setSrcOpen(false); }
  };

  const pullFrom = async (doc) => {
    try {
      const { data } = await api.post(`/documents/${src.kind}/${doc.id}/convert`);
      toast.success(`${data.number} created from ${doc.number}`);
      setSrcOpen(false);
      load();
      setViewId(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${kind}`, { params: { q, status, limit: 100 } });
      setRows(data.items);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [kind, q, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (params.get("new") && can(cfg.module, "add")) {
      setPresetLead(params.get("lead") || "");
      setEditing(null); setFormOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => { setQ(""); setStatus("all"); setViewId(null); }, [kind]);

  const columns = [
    { key: "number", label: "Number", render: (r) => <span className="num text-sm font-semibold">{r.number}</span> },
    {
      key: "party", label: cfg.party === "supplier" ? "Supplier" : "Customer",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{r.supplier_name || r.customer_name || "\u2014"}</p>
          <p className="truncate text-xs text-muted-foreground">{r.company || r.email}</p>
        </div>
      ),
    },
    { key: "date", label: "Date", render: (r) => <span className="text-sm text-muted-foreground">{fdate(r.date)}</span>, hideMobile: true },
    ...(isAdmin ? [{ key: "created_by_name", label: "Created by", render: (r) => <span className="text-sm">{r.created_by_name}</span>, hideMobile: true }] : []),
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "total", label: "Total", align: "right", render: (r) => <span className="num text-sm font-semibold">{money(r.total)}</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={cfg.icon}
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={
          <>
            {can(cfg.module, "export") && rows.length > 0 && (
              <Button variant="outline" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  Number: r.number, Date: r.date, Party: r.supplier_name || r.customer_name,
                  Status: r.status, Subtotal: r.subtotal, Tax: r.tax_total, Discount: r.discount,
                  Total: r.total, "Created By": r.created_by_name,
                })), `${kind}.csv`);
                toast.success("Exported to CSV");
              }}>
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
            {src && can(cfg.module, "add") && (
              <Button variant="soft" onClick={openSource} data-testid="doc-from-source">
                <ArrowRightLeft className="h-4 w-4" /> {src.button}
              </Button>
            )}
            {can(cfg.module, "add") && (
              <Button onClick={() => { setEditing(null); setPresetLead(""); setFormOpen(true); }} data-testid="doc-new">
                <Plus className="h-4 w-4" /> New {cfg.singular}
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="doc-search"
            placeholder={`Search ${cfg.title.toLowerCase()} by number, customer or company`} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-56"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {cfg.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable
          columns={columns}
          rows={rows}
          onRowClick={(r) => setViewId(r.id)}
          empty={<EmptyState icon={cfg.icon} title={`No ${cfg.title.toLowerCase()} yet`}
            description={`Create your first ${cfg.singular.toLowerCase()} and it will show up here with its status and value.`}
            action={can(cfg.module, "add") && (
              <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
                <Plus className="h-4 w-4" /> New {cfg.singular}
              </Button>
            )} />}
        />
      )}

      {src && (
        <Dialog open={srcOpen} onOpenChange={setSrcOpen}>
          <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto scroll-slim">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Create from a {src.label}</DialogTitle>
              <DialogDescription>
                Pick the {src.label} and everything &mdash; customer, products, prices, tax and discount &mdash;
                is carried over. Nothing is typed twice.
              </DialogDescription>
            </DialogHeader>
            {!srcRows ? (
              <div className="grid h-32 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : srcRows.length === 0 ? (
              <EmptyState title={`No ${src.label}s available`}
                description={`Create a ${src.label} first and it will be available here.`} />
            ) : (
              <div className="space-y-2">
                {srcRows.map((d) => (
                  <button key={d.id} onClick={() => pullFrom(d)} data-testid={`pull-${d.number}`}
                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-primary/50 hover:bg-primary-soft/40">
                    <span className="min-w-0">
                      <span className="num block text-sm font-semibold">{d.number}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {d.customer_name} . {d.company} . {fdate(d.date)}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="num text-sm font-semibold">{money(d.total)}</span>
                      <StatusBadge status={d.status} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      <DocumentForm
        kind={kind} open={formOpen} onOpenChange={setFormOpen} doc={editing}
        presetLeadId={presetLead} onSaved={load}
      />
      <DocumentView
        kind={kind} docId={viewId} open={!!viewId}
        onOpenChange={(v) => !v && setViewId(null)}
        onChanged={load}
        onEdit={(d) => { setViewId(null); setEditing(d); setFormOpen(true); }}
      />
    </div>
  );
}
